import { MinimalStepperProps } from '@components';
import {
    useGetTestsheetQuizMutation,
    useCreateTestsheetQuizMutation,
    TestsheetQuiz,
    CreateTestsheetQuizPayload,
    useGetUsersMutation,
    GetUsersResponse,
    useGetTestsheetsMutation,
    TestsheetModel
} from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showToast } from '@design-system';

export const useCreateTestsheetsPageVM = () => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [steps, setSteps] = useState<MinimalStepperProps['steps']>([]);
    const [form, setForm] = useState<TestsheetQuiz>([]);
    const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
    const [questionComments, setQuestionComments] = useState<Record<string, string>>({});
    const [observations, setObservations] = useState<string>('');
    const [testsheet, setTestsheet] = useState<TestsheetModel>();
    const { testsheetId } = useParams();
    const navigate = useNavigate();

    const [operators, setOperators] = useState<GetUsersResponse['operators']>([]);
    const [operatorsOptions, setOperatorsOptions] = useState<{ id: string; label: string }[]>([]);
    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);
    const [apartmentsOptions, setApartmentsOptions] = useState<{ id: string; label: string }[]>([]);
    const [buildingsOptions, setBuildingsOptions] = useState<{ id: string; label: string }[]>([]);
    const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
    const [selectedWorksiteId, setSelectedWorksiteId] = useState<string>('');
    const [selectedApartmentId, setSelectedApartmentId] = useState<string>('');
    const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');

    const [getTestsheetQuiz, { data: testsheetQuiz }] = useGetTestsheetQuizMutation();
    const [getUsers, { data: usersData }] = useGetUsersMutation();
    const [createTestsheetQuiz, { isSuccess: isCreateSuccess, isError: isCreateError, error: createError }] =
        useCreateTestsheetQuizMutation();
    const [getTestsheet, { error: errorTestsheet, data: dataTestsheet }] = useGetTestsheetsMutation();

    const [isCreating, setIsCreating] = useState(false);

    const answerLabelMap: Record<string, string> = {
        'C': 'Conforme',
        'NC': 'Non Conforme',
        'NA': 'Non Applicable'
    }

    useEffect(() => {
        getUsers();
    }, [getUsers]);

    useEffect(() => {
        if (testsheetId) {
            getTestsheet();
        }
    }, [testsheetId, getTestsheet]);

    useEffect(() => {
        if (usersData?.operators) {
            setOperators(usersData.operators);
            const options = usersData.operators.map((operator) => ({
                id: operator.id_operator,
                label: operator.name
            }));
            setOperatorsOptions(options);
        }
    }, [usersData]);

    useEffect(() => {
        if (errorTestsheet) {
            console.error(errorTestsheet);
        }
        if (dataTestsheet) {
            const currentTestsheet = dataTestsheet.find((testsheet) => testsheet.id === testsheetId);
            if (currentTestsheet) {
                setTestsheet(currentTestsheet);
            } else {
                showToast({
                    message: 'Essai/AQC non trouvé',
                    type: 'error'
                });
            }
        }
    }, [errorTestsheet, dataTestsheet, testsheetId]);

    useEffect(() => {
        if (testsheetId) {
            getTestsheetQuiz(testsheetId);
        }
    }, [testsheetId, getTestsheetQuiz]);

    useEffect(() => {
        if (testsheetQuiz) {
            setForm(testsheetQuiz);
            const newSteps: MinimalStepperProps['steps'] = [
                { label: 'Sélection du chantier', status: 'pending' },
                ...testsheetQuiz.map((item) => ({ label: item.label, status: 'pending' as const })),
                { label: 'Observations', status: 'pending' }
            ];
            setSteps(newSteps);
        }
    }, [testsheetQuiz]);

    const handleOperatorChange = (operatorId: string) => {
        setSelectedOperatorId(operatorId);
        setSelectedWorksiteId('');
        setSelectedApartmentId('');
        setSelectedBuildingId('');

        const selectedOperator = operators.find((op) => op.id_operator === operatorId);
        if (selectedOperator) {
            const worksites = selectedOperator.worksites.map((worksite) => ({
                id: worksite.id,
                label: worksite.name
            }));
            setWorksitesOptions(worksites);
        } else {
            setWorksitesOptions([]);
        }
        setApartmentsOptions([]);
        setBuildingsOptions([]);
    };

    const handleWorksiteChange = (worksiteId: string) => {
        setSelectedWorksiteId(worksiteId);
        setSelectedApartmentId('');
        setSelectedBuildingId('');

        const selectedOperator = operators.find((op) => op.id_operator === selectedOperatorId);
        if (!selectedOperator) return;

        const selectedWorksite = selectedOperator.worksites.find((ws) => ws.id === worksiteId);
        if (selectedWorksite) {
            setApartmentsOptions(
                selectedWorksite.apartments.map((apartment) => ({
                    id: apartment.id,
                    label: apartment.title
                }))
            );

            // Set buildings options if available
            if (selectedWorksite.buildings && selectedWorksite.buildings.length > 0) {
                setBuildingsOptions(
                    selectedWorksite.buildings.map((building) => ({
                        id: building.id,
                        label: building.name
                    }))
                );
            } else {
                setBuildingsOptions([]);
            }
        }
    };

    const handleApartmentChange = (apartmentId: string) => {
        setSelectedApartmentId(apartmentId);
        // Si on sélectionne un appartement, on désélectionne le bâtiment
        setSelectedBuildingId('');
    };

    const handleBuildingChange = (buildingId: string) => {
        setSelectedBuildingId(buildingId);
        // Si on sélectionne un bâtiment, on désélectionne l'appartement
        setSelectedApartmentId('');
    };

    const isSelectionStepComplete = useMemo(() => {
        return selectedOperatorId && selectedWorksiteId && (selectedApartmentId || selectedBuildingId);
    }, [selectedOperatorId, selectedWorksiteId, selectedApartmentId, selectedBuildingId]);

    const isCurrentStepComplete = useMemo(() => {
        if (currentStep === 0) {
            return isSelectionStepComplete;
        }
        if (currentStep === steps.length - 1) {
            return true;
        }
        const questionStepIndex = currentStep - 1;
        const currentQuestions = form[questionStepIndex]?.questions;
        if (!currentQuestions) return false;

        const allAnswered = currentQuestions.every((question) => selectedAnswers[question.id_question]);
        if (!allAnswered) return false;

        return currentQuestions.every((question) => {
            const answerId = selectedAnswers[question.id_question];
            const answer = question.answers.find((a) => a.id_answer === answerId);

            if (answer && answer.label === 'NC') {
                const comment = questionComments[question.id_question]?.trim();
                return comment && comment.length > 0;
            }

            return true;
        });
    }, [currentStep, steps.length, form, selectedAnswers, questionComments, isSelectionStepComplete]);

    const computedStepperSteps = useMemo(() => {
        return steps.map((step, index) => {
            if (index < currentStep) {
                return { ...step, status: 'completed' as const };
            } else if (index === currentStep) {
                return { ...step, status: 'inProgress' as const };
            } else {
                return { ...step, status: 'pending' as const };
            }
        });
    }, [steps, currentStep]);

    const selectAnswer = (questionId: string, answerId: string) => {
        setSelectedAnswers((prev) => ({
            ...prev,
            [questionId]: answerId
        }));

        const currentQuestion = form.flatMap((group) => group.questions).find((q) => q.id_question === questionId);
        const selectedAnswer = currentQuestion?.answers.find((a) => a.id_answer === answerId);
        if (selectedAnswer?.label === 'C') {
            setQuestionComments((prev) => {
                const newComments = { ...prev };
                delete newComments[questionId];
                return newComments;
            });
        }
    };

    const setQuestionComment = (questionId: string, comment: string) => {
        setQuestionComments((prev) => ({
            ...prev,
            [questionId]: comment
        }));
    };

    const canGoNext = isCurrentStepComplete;
    const canGoPrev = currentStep > 0;
    const isLastStep = currentStep === steps.length - 1;

    const handleNextStep = () => {
        if (canGoNext && currentStep < steps.length - 1) {
            const scrollContainer = document.querySelector('.rendered-page');
            if (scrollContainer) {
                scrollContainer.scrollTop = 0;
            }
            setCurrentStep((prev) => prev + 1);
        }
    };

    const handlePrevStep = () => {
        if (canGoPrev) {
            const scrollContainer = document.querySelector('.rendered-page');
            if (scrollContainer) {
                scrollContainer.scrollTop = 0;
            }
            setCurrentStep((prev) => prev - 1);
        }
    };

    const computePayload = (): CreateTestsheetQuizPayload => {
        const questions = Object.entries(selectedAnswers).map(([questionId, answerId]) => {
            const questionData: { id_question: string; id_answer: string; comment?: string } = {
                id_question: questionId,
                id_answer: answerId
            };

            const comment = questionComments[questionId]?.trim();
            if (comment) {
                questionData.comment = comment;
            }

            return questionData;
        });

        const payload: CreateTestsheetQuizPayload = {
            worksite_id: Number(selectedWorksiteId),
            test_sheet_id: Number(testsheetId),
            observations: observations.trim() || '',
            questions
        };

        // Add either apartment_id or building_id (exclusive)
        if (selectedApartmentId) {
            payload.apartment_id = Number(selectedApartmentId);
        } else if (selectedBuildingId) {
            payload.building_id = Number(selectedBuildingId);
        }

        return payload;
    };

    const handleFinish = async () => {
        try {
            setIsCreating(true);
            const payload = computePayload();
            await createTestsheetQuiz(payload);
        } catch (error) {
            console.error("Erreur lors de la création de l'essai/AQC:", error);
        } finally {
            setIsCreating(false);
        }
    };

    useEffect(() => {
        if (isCreateSuccess) {
            showToast({
                message: 'Essai/AQC créé avec succès',
                type: 'success'
            });
            navigate('/mes-chantiers/testsheets');
        }
    }, [isCreateSuccess, navigate]);

    useEffect(() => {
        if (isCreateError && createError) {
            showToast({
                message: "Erreur lors de la création de l'essai/AQC",
                type: 'error'
            });
        }
    }, [isCreateError, createError]);

    return {
        form,
        computedStepperSteps,
        currentStep,
        selectAnswer,
        selectedAnswers,
        questionComments,
        setQuestionComment,
        observations,
        setObservations,
        canGoNext,
        canGoPrev,
        isLastStep,
        handleNextStep,
        handlePrevStep,
        handleFinish,
        isCreating,
        operatorsOptions,
        worksitesOptions,
        apartmentsOptions,
        buildingsOptions,
        selectedOperatorId,
        selectedWorksiteId,
        selectedApartmentId,
        selectedBuildingId,
        handleOperatorChange,
        handleWorksiteChange,
        handleApartmentChange,
        handleBuildingChange,
        testsheet,
        answerLabelMap
    };
};
